import { useMemo, useState } from 'react';
import { markPayById, payOverview, type PayRowOv } from './prodpay-api';
import { fmtDate, mln, money } from './format';
import { useUser } from './user-context';
import { DealModal } from './DealModal';
import { PayMarkModal } from './PayMarkModal';
import { NotificationsPanel } from './NotificationsPanel';
import { downloadCSV, printRegister, registerMoney, type Col } from './export-utils';
import { useData } from './useData';

function stateText(r: PayRowOv): string {
  if (r.paid) return 'оплачено';
  switch (r.state) {
    case 'partial':
      return `частично ${money(r.paid_amount)} из ${money(r.amount)}`;
    case 'noinv':
      return 'счёт не выставлен';
    case 'over':
      return `просрочен ${-r.due_offset} дн`;
    case 'soon':
      return `через ${r.due_offset} дн`;
    default:
      return `план +${r.due_offset} дн`;
  }
}

const PAY_COLS: Col<PayRowOv>[] = [
  { header: 'Клиент', value: (r) => r.client },
  { header: 'Контракт', value: (r) => r.contract_no ?? r.no },
  { header: 'Транш', value: (r) => r.label },
  { header: 'Сумма, ₸', value: (r) => r.amount, print: (r) => registerMoney(r.amount), align: 'r' },
  { header: 'Статус', value: (r) => stateText(r) },
  { header: 'Срок', value: (r) => (r.paid ? '' : fmtDate(r.due_date)), align: 'r' },
];

function stateTag(r: PayRowOv) {
  if (r.paid) return <span className="chip grn">оплачено</span>;
  switch (r.state) {
    case 'partial':
      return <span className="chip gold">частично · {money(r.paid_amount)}</span>;
    case 'noinv':
      return <span className="chip red">счёт не выставлен</span>;
    case 'over':
      return <span className="chip red">просрочен {-r.due_offset} дн</span>;
    case 'soon':
      return <span className="chip gold">через {r.due_offset} дн</span>;
    default:
      return <span className="chip">план +{r.due_offset} дн</span>;
  }
}

// Ранг срочности статуса — для сортировки «по статусу» (сначала самые срочные).
const STATE_RANK: Record<string, number> = { noinv: 0, over: 1, partial: 2, soon: 3, plan: 4 };
const urgency = (r: PayRowOv): number => (r.paid ? 100 : (STATE_RANK[r.state] ?? 50));

type SortKey = 'client' | 'label' | 'amount' | 'due' | 'status';
type StatusFilter = 'all' | 'unpaid' | 'paid' | 'over' | 'noinv' | 'soon' | 'plan';

const STATUS_OPTS: { v: StatusFilter; t: string }[] = [
  { v: 'all', t: 'Все статусы' },
  { v: 'unpaid', t: 'Не оплачено' },
  { v: 'paid', t: 'Оплачено' },
  { v: 'over', t: 'Просрочено' },
  { v: 'noinv', t: 'Без счёта' },
  { v: 'soon', t: 'Скоро (≤14 дн)' },
  { v: 'plan', t: 'Плановые' },
];

// Направление по умолчанию при первом клике по колонке.
const DEFAULT_DIR: Record<SortKey, 'asc' | 'desc'> = {
  client: 'asc',
  label: 'asc',
  amount: 'desc',
  due: 'asc',
  status: 'asc',
};

function matchesStatus(r: PayRowOv, f: StatusFilter): boolean {
  if (f === 'all') return true;
  if (f === 'paid') return !!r.paid;
  if (f === 'unpaid') return !r.paid;
  return !r.paid && r.state === f;
}

export function PayScreen() {
  const user = useUser();
  const { data, refresh } = useData('pay', payOverview);
  const [openId, setOpenId] = useState<number | null>(null);
  const [markRow, setMarkRow] = useState<PayRowOv | null>(null); // окно отметки оплаты
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const canPay = user.screens.includes('pay');

  function toggleSort(k: SortKey) {
    if (k === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(k);
      setSortDir(DEFAULT_DIR[k]);
    }
  }

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const out = data.rows.filter((r) => {
      if (!matchesStatus(r, status)) return false;
      if (!needle) return true;
      const hay = `${r.client} ${r.contract_no ?? r.no} ${r.label}`.toLowerCase();
      return hay.includes(needle);
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      let c = 0;
      switch (sortKey) {
        case 'client':
          c = a.client.localeCompare(b.client, 'ru');
          break;
        case 'label':
          c = a.label.localeCompare(b.label, 'ru');
          break;
        case 'amount':
          c = a.amount - b.amount;
          break;
        case 'due':
          // Оплаченные транши — в конец списка независимо от направления.
          if (!!a.paid !== !!b.paid) return a.paid ? 1 : -1;
          c = a.due_offset - b.due_offset;
          break;
        case 'status':
          c = urgency(a) - urgency(b) || a.due_offset - b.due_offset;
          break;
      }
      if (c === 0) c = a.client.localeCompare(b.client, 'ru');
      return c * dir;
    });
    return out;
  }, [data, q, status, sortKey, sortDir]);

  if (!data) return <p className="muted">Загрузка…</p>;

  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const thProps = (k: SortKey) => ({
    onClick: () => toggleSort(k),
    className: 'sortable' + (sortKey === k ? ' on' : ''),
  });

  function filterNote(): string {
    const parts: string[] = [];
    if (q.trim()) parts.push(`поиск «${q.trim()}»`);
    const st = STATUS_OPTS.find((o) => o.v === status);
    if (status !== 'all' && st) parts.push(st.t);
    return parts.length ? 'Фильтр: ' + parts.join(', ') : 'Все платежи';
  }
  function exportCSV() {
    downloadCSV('платежи', PAY_COLS, rows);
  }
  function printReg() {
    const total = rows.reduce((s, r) => s + r.amount, 0);
    printRegister({
      title: 'Реестр платежей',
      subtitle: filterNote(),
      cols: PAY_COLS,
      rows,
      totals: ['Итого', '', '', registerMoney(total), '', ''],
    });
  }

  return (
    <div className="scr">
      <div className="scr-head">
        <h2>Платежи и дебиторка</h2>
        <span className="muted">контроль оплат по этапам</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button
            className="btn ghost sm"
            disabled={rows.length === 0}
            onClick={exportCSV}
            title="Скачать CSV (Excel)"
          >
            ⬇ CSV
          </button>
          <button
            className="btn ghost sm"
            disabled={rows.length === 0}
            onClick={printReg}
            title="Печатный реестр"
          >
            🖨 Реестр
          </button>
        </div>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="kl">Дебиторка всего</div>
          <div className="kv cy">{mln(data.kpi.debitor)} ₸</div>
        </div>
        <div className="kpi">
          <div className="kl">Просрочка / без счёта</div>
          <div className="kv red">{mln(data.kpi.overdue)} ₸</div>
        </div>
        <div className="kpi">
          <div className="kl">К оплате ≤14 дней</div>
          <div className="kv gold">{mln(data.kpi.soon)} ₸</div>
        </div>
      </div>

      <NotificationsPanel onOpenDeal={setOpenId} />

      <div className="filters">
        <input
          className="filter-search"
          type="search"
          placeholder="Поиск: клиент, контракт, транш…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
          {STATUS_OPTS.map((o) => (
            <option key={o.v} value={o.v}>
              {o.t}
            </option>
          ))}
        </select>
        <span className="muted small filter-count">
          {rows.length} из {data.rows.length}
        </span>
      </div>

      <div className="tablewrap glass" style={{ marginTop: 12 }}>
        <table className="tbl card-table">
          <thead>
            <tr>
              <th {...thProps('client')}>Клиент{arrow('client')}</th>
              <th>Контракт</th>
              <th {...thProps('label')}>Транш{arrow('label')}</th>
              <th {...thProps('amount')} className={thProps('amount').className + ' r'}>
                Сумма{arrow('amount')}
              </th>
              <th {...thProps('status')}>Статус{arrow('status')}</th>
              <th {...thProps('due')}>Срок{arrow('due')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.pay_id} onClick={() => setOpenId(r.deal_id)}>
                <td data-label="Клиент">
                  <b>{r.client}</b>
                </td>
                <td data-label="Контракт" className="mono muted">
                  {r.contract_no ?? r.no}
                </td>
                <td data-label="Транш">{r.label}</td>
                <td data-label="Сумма" className="r mono">
                  {money(r.amount)}
                  {!r.paid && r.paid_amount > 0 && (
                    <div className="small" style={{ color: 'var(--gold)' }}>
                      получено {money(r.paid_amount)}
                    </div>
                  )}
                </td>
                <td data-label="Статус">{stateTag(r)}</td>
                <td
                  data-label="Срок"
                  className="mono muted"
                  title={
                    r.paid
                      ? ''
                      : r.due_offset >= 0
                        ? `через ${r.due_offset} дн`
                        : `просрочен ${-r.due_offset} дн`
                  }
                >
                  {r.paid ? '—' : fmtDate(r.due_date)}
                </td>
                <td className="r">
                  {!r.paid && canPay && (
                    <button
                      className="btn ghost sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMarkRow(r);
                      }}
                    >
                      {r.paid_amount > 0 ? 'Изменить оплату' : 'Отметить оплату'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="muted">
                  Ничего не найдено по заданным фильтрам.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openId != null && (
        <DealModal
          dealId={openId}
          screens={user.screens}
          onClose={() => setOpenId(null)}
          onChanged={refresh}
        />
      )}

      {markRow && (
        <PayMarkModal
          dealNo={markRow.contract_no ?? markRow.no}
          label={markRow.label}
          amount={markRow.amount}
          paidAmount={markRow.paid_amount}
          paidRef={markRow.paid_ref}
          onClose={() => setMarkRow(null)}
          onSubmit={async (amount, ref) => {
            await markPayById(markRow.deal_id, markRow.pay_id, { amount, ref });
            setMarkRow(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}
